import styled from 'styled-components';

import { ButtonProps, ButtonStyleProps } from './types';

const getKey = (
  key: string,
  prefix?: string,
  postfix?: string
): keyof ButtonProps['theme'] => {
  if (prefix) {
    return `${prefix}${key}${postfix ?? ''}` as keyof ButtonProps['theme'];
  }
  return `${key.charAt(0).toLowerCase() + key.slice(1)}${
    postfix ?? ''
  }` as keyof ButtonProps['theme'];
};

const getColorWrapper =
  (
    prefix?: string,
    postfix?: string,
    propsOverride?: Partial<ButtonStyleProps>
  ): ((props: ButtonStyleProps) => string) =>
  (actualProps: ButtonStyleProps): string => {
    const props = { ...actualProps, ...propsOverride };
    if (!props.$theme) {
      return '';
    }

    let result: string = '';

    if (props.$disabled && !props.$loading) {
      result = props.$theme[getKey('DisabledColor', prefix, postfix)];
    } else {
      const activeColor = props.$theme[getKey('ActiveColor', prefix, postfix)];

      // if no activeColor is defined for this prefix, return default color below (used for border-color)
      if (props.$isActive && (activeColor || !postfix)) {
        result = activeColor;
      } else {
        result = props.$theme[getKey('Color', prefix, postfix)];
      }
    }

    if (result) {
      return result;
    } else if (postfix) {
      return getColorWrapper(prefix)(props);
    } else {
      return '';
    }
  };

export const Label = styled.div<ButtonStyleProps>`
  cursor: inherit;
  font-size: ${({ $theme }): string | undefined => $theme.fontSize};
  font-weight: ${({ $theme, $isActive }): number | undefined =>
    $isActive ? $theme.fontWeightActive : $theme.fontWeight};
  width: ${({ $theme }): string | undefined => $theme.labelWidth};

  transition: font-weight 225ms ease;
`;

export const ButtonContainer = styled.div<ButtonStyleProps>`
  display: flex;

  align-items: center;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  opacity: 1;
  border-radius: ${({ $theme }): string | undefined => $theme.borderRadius};

  background: ${getColorWrapper('background')};
  color: ${getColorWrapper()};
  ${({ $theme }): string =>
    ($theme.borderBottom ? 'border-bottom' : 'border') +
    `: ${$theme.borderWidth ?? '1px'} solid;`};
  border-color: ${getColorWrapper('border')};
  box-shadow: ${({ $theme }): string => $theme.boxShadow ?? 'unset'};

  *:focus-visible > &,
  *:active > &,
  &:hover {
    background: ${getColorWrapper('background', undefined, {
      $isActive: true,
    })};
    color: ${getColorWrapper(undefined, undefined, { $isActive: true })};
    border-color: ${getColorWrapper('border', undefined, { $isActive: true })};
    box-shadow: ${({ $theme }): string => $theme.boxShadowActive ?? 'unset'};

    ${Label} {
      font-weight: ${({ $theme }): number | string =>
        $theme.fontWeightActive ?? 'unset'};
    }
  }

  html[data-theme='dark'] &&,
  .dark && {
    background: ${getColorWrapper('background', 'Dark')};
    color: ${getColorWrapper(undefined, 'Dark')};
    border-color: ${getColorWrapper('border', 'Dark')};

    &:hover {
      background: ${getColorWrapper('background', 'Dark', { $isActive: true })};
      color: ${getColorWrapper(undefined, 'Dark', { $isActive: true })};
      border-color: ${getColorWrapper('border', 'Dark', { $isActive: true })};
    }
  }
`;

export const Content = styled.div<ButtonStyleProps>`
  display: flex;

  width: 100%;
  padding: 0 ${({ $theme }): string => $theme.padding ?? 'unset'};
  box-sizing: border-box;
  justify-content: ${({ $isCentered }): string =>
    $isCentered ? 'center' : 'space-between'};
  align-items: center;
  position: relative;

  opacity: ${({ $disabled }): string => ($disabled ? '0.5' : '1')};
  html[data-theme='dark'] &&,
  .dark && {
    opacity: ${({ $disabled }): string => ($disabled ? '0.3' : '1')};
  }
`;

export const InputContent = styled.div<ButtonStyleProps>`
  display: flex;

  align-items: center;
  gap: ${({ $theme }): string => $theme.gap ?? '10px'};

  justify-content: ${({ $isCentered }): string =>
    $isCentered ? 'center' : 'unset'};
  flex-grow: ${({ $isCentered }): number => ($isCentered ? 1 : 0)};
`;

export const IconWrapper = styled.div<ButtonStyleProps>`
  display: flex;

  min-width: ${({ $theme }): string => $theme.iconMinWidth ?? '0px'};
  justify-content: space-around;
`;

export const Caret = styled.div``;
